/**
 * editMutations.js — geometric transform mutations
 * rotate, scale, mirror, copy, trim, array (rect + polar)
 */
import { Geo } from "../utils/geo";
import { intP } from "../utils/coords";
import { splitEdgePrim, removeEdgePrim } from "./dcel";
import { mutAddWall } from "./mutations";
import { healGraph } from "./heal";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Rotate point p around pivot by angle radians */
function rotPt(p, pivot, angle) {
  const cos = Math.cos(angle), sin = Math.sin(angle);
  const dx = p.x - pivot.x, dy = p.y - pivot.y;
  return {
    x: Math.round(pivot.x + dx * cos - dy * sin),
    y: Math.round(pivot.y + dx * sin + dy * cos),
  };
}

/** Scale point p around pivot by factor */
function scalePt(p, pivot, factor) {
  return {
    x: Math.round(pivot.x + (p.x - pivot.x) * factor),
    y: Math.round(pivot.y + (p.y - pivot.y) * factor),
  };
}

/** Mirror point across axis line (axisA → axisB) */
function mirrorPt(p, axisA, axisB) {
  const dx = axisB.x - axisA.x, dy = axisB.y - axisA.y;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-9) return { ...p };
  const t = ((p.x - axisA.x) * dx + (p.y - axisA.y) * dy) / len2;
  const foot = { x: axisA.x + t * dx, y: axisA.y + t * dy };
  return {
    x: Math.round(2 * foot.x - p.x),
    y: Math.round(2 * foot.y - p.y),
  };
}

/** Collect edge data for selected edge ids */
function edgeSegments(edgeIds, st) {
  return edgeIds.flatMap(eId => {
    const e = st.edges[eId]; if (!e) return [];
    const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
    if (!ab || !ba) return [];
    const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
    if (!nA || !nB) return [];
    return [{ nA, nB, e }];
  });
}

/** centroid of a set of node points */
function centroidOfNodes(nodeIds, st) {
  const pts = nodeIds.map(id => st.nodes[id]).filter(Boolean);
  if (!pts.length) return { x: 0, y: 0 };
  return {
    x: Math.round(pts.reduce((s, p) => s + p.x, 0) / pts.length),
    y: Math.round(pts.reduce((s, p) => s + p.y, 0) / pts.length),
  };
}

// ─── Rotate ───────────────────────────────────────────────────────────────────

/**
 * Rotate a set of nodes (and all their connected edges) around a pivot.
 * selMany: { nodes, edges }
 * pivot: { x, y } world-mm (defaults to centroid of selected nodes)
 * angleDeg: rotation angle in degrees (CCW positive)
 */
export function mutRotate(selMany, angleDeg, pivot, st) {
  if (!angleDeg) return st;
  const angle = (angleDeg * Math.PI) / 180;

  // Collect all node ids involved (from explicit node list + edge endpoints)
  const nodeSet = new Set(selMany.nodes || []);
  (selMany.edges || []).forEach(eId => {
    const e = st.edges[eId]; if (!e) return;
    const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
    if (ab) nodeSet.add(ab.originId);
    if (ba) nodeSet.add(ba.originId);
  });
  const nodeIds = [...nodeSet];
  if (!nodeIds.length) return st;

  const piv = pivot || centroidOfNodes(nodeIds, st);
  const newNodes = { ...st.nodes };
  for (const id of nodeIds) {
    const n = newNodes[id]; if (!n) continue;
    newNodes[id] = { ...n, ...rotPt(n, piv, angle) };
  }
  const s = { ...st, nodes: newNodes };
  const { state, errors } = healGraph(s);
  return { ...state, _errors: errors };
}

// ─── Scale ────────────────────────────────────────────────────────────────────

/**
 * Scale selected nodes around a pivot.
 * factor: e.g. 2.0 = double size, 0.5 = half
 */
export function mutScale(selMany, factor, pivot, st) {
  if (!factor || factor <= 0) return st;

  const nodeSet = new Set(selMany.nodes || []);
  (selMany.edges || []).forEach(eId => {
    const e = st.edges[eId]; if (!e) return;
    const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
    if (ab) nodeSet.add(ab.originId);
    if (ba) nodeSet.add(ba.originId);
  });
  const nodeIds = [...nodeSet];
  if (!nodeIds.length) return st;

  const piv = pivot || centroidOfNodes(nodeIds, st);
  const newNodes = { ...st.nodes };
  for (const id of nodeIds) {
    const n = newNodes[id]; if (!n) continue;
    newNodes[id] = { ...n, ...scalePt(n, piv, factor) };
  }
  const s = { ...st, nodes: newNodes };
  const { state, errors } = healGraph(s);
  return { ...state, _errors: errors };
}

// ─── Mirror ───────────────────────────────────────────────────────────────────

/**
 * Mirror selected edges/nodes across an axis defined by two points.
 * Creates new geometry (copy + mirror), does not remove originals.
 * Pass deleteSrc: true to replace original with mirror.
 */
export function mutMirror(selMany, axisA, axisB, deleteSrc, st) {
  const segs = edgeSegments(selMany.edges || [], st);
  if (!segs.length) return st;

  let s = st;

  // Optionally remove originals
  if (deleteSrc) {
    for (const { e } of segs) {
      s = removeEdgePrim(e.id, s);
    }
  }

  // Add mirrored walls
  for (const { nA, nB, e } of segs) {
    const mA = mirrorPt(nA, axisA, axisB);
    const mB = mirrorPt(nB, axisA, axisB);
    s = mutAddWall(mA, mB, e.type || "wall", e.thickness || 230, e.height || 3000, e.materialId || "BRICK", s);
  }

  return s;
}

// ─── Copy / move selection ────────────────────────────────────────────────────

/**
 * Copy selected edges to a new position offset by (dx, dy).
 * Original edges remain intact.
 */
export function mutCopyEdges(edgeIds, dx, dy, st) {
  if (!edgeIds?.length) return st;
  let s = st;
  for (const eId of edgeIds) {
    const e = st.edges[eId]; if (!e) continue;
    const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
    if (!ab || !ba) continue;
    const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
    if (!nA || !nB) continue;
    s = mutAddWall(
      { x: nA.x + dx, y: nA.y + dy },
      { x: nB.x + dx, y: nB.y + dy },
      e.type || "wall", e.thickness || 230, e.height || 3000, e.materialId || "BRICK",
      s,
    );
  }
  return s;
}

// ─── Trim ─────────────────────────────────────────────────────────────────────

/**
 * Trim edgeId at its intersection with cutEdgeId.
 * keepSide: "A" = keep the A-origin side, "B" = keep B-origin side.
 *
 * Strategy: find intersection point, split both edges at it, remove
 * the unwanted half-segment of the target edge.
 */
export function mutTrim(edgeId, cutEdgeId, keepSide, st) {
  const e   = st.edges[edgeId];
  const cut = st.edges[cutEdgeId];
  if (!e || !cut || edgeId === cutEdgeId) return st;

  const ab  = st.halfEdges[e.halfEdgeA],   ba  = st.halfEdges[e.halfEdgeB];
  const cab = st.halfEdges[cut.halfEdgeA], cba = st.halfEdges[cut.halfEdgeB];
  if (!ab || !ba || !cab || !cba) return st;

  const nA  = st.nodes[ab.originId],  nB  = st.nodes[ba.originId];
  const cnA = st.nodes[cab.originId], cnB = st.nodes[cba.originId];
  if (!nA || !nB || !cnA || !cnB) return st;

  const ix = Geo.sx(nA, nB, cnA, cnB);
  if (!ix || ix.t < 0.005 || ix.t > 0.995) return st; // no valid intersection

  const ixPt = intP({ x: ix.x, y: ix.y });

  // Split the target edge at intersection
  let s = st;
  const { st: s2, mid } = splitEdgePrim(edgeId, ixPt, s);
  s = s2;
  if (!mid) return st;

  // Find which of the two new sub-edges is the part to remove
  // After split, two new edges connect: [A→mid] and [mid→B]
  // keepSide "A" = keep A→mid (remove mid→B)
  // keepSide "B" = keep mid→B (remove A→mid)
  const newEdges = Object.values(s.edges).filter(ne => {
    const nab = s.halfEdges[ne.halfEdgeA], nba = s.halfEdges[ne.halfEdgeB];
    if (!nab || !nba) return false;
    return nab.originId === mid || nba.originId === mid;
  });

  for (const ne of newEdges) {
    const nab = s.halfEdges[ne.halfEdgeA];
    if (!nab) continue;
    const isAside = nab.originId !== mid; // starts from original A
    const shouldRemove = keepSide === "A" ? !isAside : isAside;
    if (shouldRemove) {
      s = removeEdgePrim(ne.id, s);
    }
  }

  const { state, errors } = healGraph(s);
  return { ...state, _errors: errors };
}

// ─── Rectangular Array ────────────────────────────────────────────────────────

/**
 * Create a rectangular array of selected edges.
 * cols, rows: count (including original)
 * colSpacing, rowSpacing: offset per step in mm
 */
export function mutArrayRect(edgeIds, cols, rows, colSpacingMm, rowSpacingMm, st) {
  if (!edgeIds?.length) return st;
  let s = st;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (row === 0 && col === 0) continue; // skip original position
      const dx = col * colSpacingMm;
      const dy = row * rowSpacingMm;
      s = mutCopyEdges(edgeIds, dx, dy, s);
    }
  }
  return s;
}

// ─── Polar Array ──────────────────────────────────────────────────────────────

/**
 * Create a polar (circular) array of selected edges.
 * count: total number of items (including original)
 * center: { x, y } pivot point
 * totalAngleDeg: total arc angle (360 for full circle)
 */
export function mutArrayPolar(edgeIds, count, center, totalAngleDeg, st) {
  if (!edgeIds?.length || count < 2) return st;
  const stepAngle = (totalAngleDeg / count) * (Math.PI / 180);
  let s = st;

  for (let i = 1; i < count; i++) {
    const angle = stepAngle * i;
    const cos = Math.cos(angle), sin = Math.sin(angle);
    for (const eId of edgeIds) {
      const e = st.edges[eId]; if (!e) continue;
      const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
      if (!ab || !ba) continue;
      const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
      if (!nA || !nB) continue;
      const rA = rotPt(nA, center, angle);
      const rB = rotPt(nB, center, angle);
      s = mutAddWall(rA, rB, e.type || "wall", e.thickness || 230, e.height || 3000, e.materialId || "BRICK", s);
    }
  }
  return s;
}
